import React from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../../../../components/base/PageLayout';

const Communication: React.FC = () => {
  React.useEffect(() => {
    document.title = '執事のコミュニケーション術 - 心に響く会話と信頼を築く対話の技法｜執事ベル';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '執事ベルが実践するコミュニケーション術。相手の心に寄り添う傾聴の技術、適切な言葉選び、非言語コミュニケーション、信頼関係を構築する対話法など、プロフェッショナル執事ならではの人間関係構築のノウハウを解説。ビジネスシーンでの商談、接客、部下育成から、プライベートの人間関係まで幅広く活用できるコミュニケーションスキルをお伝えします。');
    }
  }, []);

  return (
    <PageLayout hero={<>
<section className="relative py-16 md:py-32 min-h-[420px] md:min-h-[560px] overflow-hidden bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a]">
          <img src="/images/generated/seq-communication-intro-5c5c91.jpg" alt="応接室で依頼主と向き合い、耳を傾ける執事" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="mb-6">
                <span className="inline-block bg-amber-400/20 text-amber-300 px-5 sm:px-6 lg:px-8 py-2 rounded-full text-sm font-medium">
                  執事の知恵
                </span>
              </div>
              <h1 className="text-4xl md:text-6xl font-serif text-white mb-6">
                コミュニケーション術
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed">
                心を通わせる会話の技術と信頼関係構築の秘訣
              </p>
              <div className="mt-8 text-sm text-gray-300">
                執筆：執事ベル（全日本執事協会 3代目頭取）
              </div>
            </div>
          </div>
        </section>
</>}>
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
        {/* Hero Section */}
        

        {/* Article Content */}
        <article className="py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <img
                  src="/images/generated/seq-communication-intro-5c5c91.jpg"
                  alt="コミュニケーションの様子"
                  className="w-full aspect-[16/9] h-auto object-cover object-top rounded-lg shadow-lg mb-8"
                />
                
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  執事として30年間、様々なお客様とのコミュニケーションを通じて学んだことは、真のコミュニケーションとは単なる情報の伝達ではなく、心と心を繋ぐ架け橋だということです。相手の立場に立ち、その方の気持ちを理解し、適切な言葉と態度で応答する技術をお伝えいたします。
                </p>
              </div>

              {/* Section 1 */}
              <section className="mb-12">
                <h2 className="text-3xl font-serif text-gray-900 mb-6">聞く技術の極意</h2>
                
                <h3 className="text-2xl font-serif text-gray-800 mb-4">アクティブリスニングの実践</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  優れたコミュニケーションの基盤は「聞く」ことにあります。ただ耳を傾けるのではなく、相手の言葉の背後にある感情や真意を読み取る「アクティブリスニング」が重要です。
                </p>
                
                <div className="bg-amber-50 p-6 rounded-lg mb-6">
                  <h4 className="text-lg font-medium text-amber-800 mb-3">アクティブリスニングの5つのポイント</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li><strong>1. 全身で聞く</strong> - 相手に体を向け、アイコンタクトを保つ</li>
                    <li><strong>2. 先入観を捨てる</strong> - 判断せずに、まず相手の話を受け入れる</li>
                    <li><strong>3. 感情を読み取る</strong> - 言葉だけでなく、表情や声のトーンに注意</li>
                    <li><strong>4. 適切な相槌</strong> - 理解していることを示す反応を返す</li>
                    <li><strong>5. 要約と確認</strong> - 理解した内容を相手に確認する</li>
                  </ul>
                </div>

                <h3 className="text-2xl font-serif text-gray-800 mb-4">沈黙の活用</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  多くの人は沈黙を恐れますが、適切な沈黙は相手に考える時間を与え、より深い話を引き出す効果があります。相手が話し終わった後、2〜3秒待つことで、追加の情報や本音を聞くことができます。
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-12">
                <h2 className="text-3xl font-serif text-gray-900 mb-6">話す技術の向上</h2>
                
                <img
                  src="/images/generated/seq-speaking-skills-690627.jpg"
                  alt="話す技術"
                  className="w-full aspect-[16/9] h-auto object-cover object-top rounded-lg shadow-lg mb-8"
                />

                <h3 className="text-2xl font-serif text-gray-800 mb-4">言葉選びの重要性</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  同じ内容でも、使う言葉によって相手に与える印象は大きく変わります。特に執事として大切なのは、相手の立場や感情に配慮した言葉選びです。
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-red-50 p-6 rounded-lg">
                    <h4 className="text-lg font-medium text-red-800 mb-3">避けるべき表現</h4>
                    <ul className="space-y-2 text-red-700 text-sm">
                      <li>• 「でも」「しかし」（否定的な印象）</li>
                      <li>• 「絶対に」「必ず」（断定的すぎる）</li>
                      <li>• 「普通は」「一般的には」（相手を否定する可能性）</li>
                      <li>• 「忙しい」「時間がない」（相手を軽視する印象）</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h4 className="text-lg font-medium text-green-800 mb-3">効果的な表現</h4>
                    <ul className="space-y-2 text-green-700 text-sm">
                      <li>• 「そうですね、そして」（肯定的な接続）</li>
                      <li>• 「〜かもしれません」（柔らかい提案）</li>
                      <li>• 「お客様の場合は」（個別性を重視）</li>
                      <li>• 「お時間をいただき」（感謝の表現）</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-2xl font-serif text-gray-800 mb-4">声のトーンと話すペース</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  言葉の内容と同じくらい重要なのが、声のトーンと話すペースです。相手の状況や感情に合わせて調整することで、より効果的なコミュニケーションが可能になります。
                </p>

                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">状況別の声のトーン調整</h4>
                  <ul className="space-y-3 text-gray-700">
                    <li><strong>緊急時</strong> - 落ち着いた低めのトーンで安心感を与える</li>
                    <li><strong>お祝い事</strong> - 明るく温かみのあるトーンで喜びを共有</li>
                    <li><strong>相談事</strong> - 優しく包み込むようなトーンで安心感を提供</li>
                    <li><strong>指示・説明</strong> - 明瞭で聞き取りやすいトーンで確実に伝達</li>
                  </ul>
                </div>
              </section>

              {/* Section 3 */}
              <section className="mb-12">
                <h2 className="text-3xl font-serif text-gray-900 mb-6">非言語コミュニケーション</h2>
                
                <h3 className="text-2xl font-serif text-gray-800 mb-4">ボディランゲージの力</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  研究によると、コミュニケーションにおいて言葉が占める割合は7%に過ぎず、残りの93%は声のトーン（38%）とボディランゲージ（55%）が占めています。執事として、この非言語コミュニケーションを意識的にコントロールすることが重要です。
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">姿勢</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• 背筋を伸ばし、品格を保つ</li>
                      <li>• 相手に体を向ける</li>
                      <li>• 適度な距離を保つ</li>
                      <li>• 威圧感を与えない</li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">表情</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• 自然で温かい笑顔</li>
                      <li>• 相手の感情に共感</li>
                      <li>• 真剣さを表現</li>
                      <li>• 安心感を与える</li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">手の動き</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• 優雅で無駄のない動作</li>
                      <li>• 適切なジェスチャー</li>
                      <li>• 落ち着いた手の位置</li>
                      <li>• 相手を指さない</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-2xl font-serif text-gray-800 mb-4">アイコンタクトの技術</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  適切なアイコンタクトは信頼関係の構築に不可欠です。しかし、文化的背景や個人の性格によって快適に感じる視線の強さは異なるため、相手の反応を見ながら調整することが重要です。
                </p>
              </section>

              {/* Section 4 */}
              <section className="mb-12">
                <h2 className="text-3xl font-serif text-gray-900 mb-6">困難な状況でのコミュニケーション</h2>
                
                <img
                  src="/images/generated/seq-difficult-communication-e5f100.jpg"
                  alt="困難な状況でのコミュニケーション"
                  className="w-full aspect-[16/9] h-auto object-cover object-top rounded-lg shadow-lg mb-8"
                />

                <h3 className="text-2xl font-serif text-gray-800 mb-4">感情的な相手への対応</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  お客様が感情的になられた際の対応は、執事として最も重要なスキルの一つです。まず相手の感情を受け止め、共感を示すことから始めます。
                </p>

                <div className="bg-amber-50 p-6 rounded-lg mb-6">
                  <h4 className="text-lg font-medium text-amber-800 mb-3">感情的な相手への対応ステップ</h4>
                  <ol className="space-y-3 text-gray-700">
                    <li><strong>1. 冷静さを保つ</strong> - 自分の感情をコントロールし、落ち着いて対応</li>
                    <li><strong>2. 共感を示す</strong> - 「お気持ちはよく分かります」など、理解を示す</li>
                    <li><strong>3. 事実を整理</strong> - 感情と事実を分けて、問題の本質を把握</li>
                    <li><strong>4. 解決策を提示</strong> - 具体的で実現可能な解決策を提案</li>
                    <li><strong>5. フォローアップ</strong> - 解決後も継続的にケアを提供</li>
                  </ol>
                </div>

                <h3 className="text-2xl font-serif text-gray-800 mb-4">断りの技術</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  時には、お客様のご要望にお応えできない場合もあります。そのような際も、相手の気持ちを傷つけることなく、理解していただく技術が必要です。
                </p>

                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <h4 className="text-lg font-medium text-gray-900 mb-3">効果的な断り方の例</h4>
                  <p className="text-gray-700 mb-3">
                    <strong>悪い例：</strong>「それはできません」「規則ですので」
                  </p>
                  <p className="text-gray-700">
                    <strong>良い例：</strong>「申し訳ございませんが、〜の理由により、そのままではお受けできかねます。しかし、〜という方法でしたら、ご希望に近い形でお手伝いできるかもしれません」
                  </p>
                </div>
              </section>

              {/* Section 5 */}
              <section className="mb-12">
                <h2 className="text-3xl font-serif text-gray-900 mb-6">文化的配慮とグローバルコミュニケーション</h2>
                
                <h3 className="text-2xl font-serif text-gray-800 mb-4">文化的差異の理解</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  国際的なお客様とのコミュニケーションでは、文化的な違いを理解し、尊重することが重要です。同じ言葉でも、文化によって受け取り方が異なる場合があります。
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">西洋文化圏</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• 直接的なコミュニケーションを好む</li>
                      <li>• アイコンタクトを重視</li>
                      <li>• 個人の意見を尊重</li>
                      <li>• 時間に対して厳格</li>
                    </ul>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h4 className="text-lg font-medium text-gray-900 mb-3">東洋文化圏</h4>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li>• 間接的な表現を好む</li>
                      <li>• 謙虚さを重視</li>
                      <li>• 集団の調和を大切にする</li>
                      <li>• 上下関係を意識</li>
                    </ul>
                  </div>
                </div>

                <h3 className="text-2xl font-serif text-gray-800 mb-4">言語の壁を越える技術</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  言語が異なる相手とのコミュニケーションでは、言葉以外の方法も活用します。ジェスチャー、表情、図解などを組み合わせることで、より効果的に意思疎通を図ることができます。
                </p>
              </section>

              {/* Conclusion */}
              <section className="mb-12">
                <h2 className="text-3xl font-serif text-gray-900 mb-6">まとめ</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  優れたコミュニケーション能力は、一朝一夕に身につくものではありません。日々の実践と振り返りを通じて、少しずつ向上させていくものです。相手の立場に立ち、心を込めて接することで、真の信頼関係を築くことができます。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  執事として最も大切なのは、技術的なスキルよりも、相手を思いやる心です。その心があれば、自然と適切な言葉と行動が生まれ、お客様との深い絆を築くことができるでしょう。
                </p>
              </section>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif text-gray-900 mb-4">関連記事</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: 'VIP対応の極意',
                  description: '特別なお客様への最高のサービス',
                  path: '/wisdom/articles/vip-service',
                  image: '/images/generated/seq-vip-related-5e61af.jpg'
                },
                {
                  title: '言葉遣いと話し方',
                  description: '品格ある言葉の使い方',
                  path: '/wisdom/articles/speech',
                  image: '/images/generated/seq-speech-related-b2211b.jpg'
                },
                {
                  title: '社交のエチケット',
                  description: '社交場での振る舞い方',
                  path: '/wisdom/articles/social-etiquette',
                  image: '/images/generated/seq-social-related-598270.jpg'
                }
              ].map((article, index) => (
                <Link key={index} to={article.path} className="group">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-serif text-gray-900 mb-2 group-hover:text-amber-700 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {article.description}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-amber-700 to-amber-800">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
              コミュニケーションスキルを向上させませんか
            </h2>
            <p className="text-xl text-amber-100 mb-8 leading-relaxed">
              実践的なコミュニケーション研修で、お客様との信頼関係構築力を高めましょう
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-amber-800 px-8 py-4 rounded-lg font-medium hover:bg-amber-50 transition-colors whitespace-nowrap"
            >
              研修のお問い合わせ
            </Link>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default Communication;